import taskService from 'resources/task/task.service';
import dealService from 'resources/deal/deal.service';
import documentService from 'resources/document/document.service';

import * as documentOperations from 'resources/document/document.operations';

import * as userHelpers from 'resources/user/user.helpers';
import * as taskHelpers from 'resources/task/task.helpers';

import { getMilestonesWithTasksByDealId } from 'resources/milestone/workflow/getMilestonesWithTasks';

const getTransactionDocuments = async (dealId, user) => {
  const transactionDocumentIds = await dealService.getTransactionDocumentIds(dealId);

  const transactionDocuments = await documentService.findByIds(transactionDocumentIds) || [];

  const filteredTransactionDocuments = (user && userHelpers.isClient(user))
    ? transactionDocuments.filter((document) => !document.is_hidden_from_client)
    : transactionDocuments;

  return filteredTransactionDocuments;
};

const getTaskDocumentsByDealId = async (dealId, user) => {
  const allTasks = (user && userHelpers.isClient(user))
    ? await taskService.findClientTasksByDealId(dealId)
    : await taskService.getTasksByDealId(dealId);

  if (!allTasks) {
    return [];
  }

  const dealDocuments = [];

  const tasks = allTasks.filter((task) => taskHelpers.isDefault(task));
  const taskIds = tasks.map((task) => task.id);
  const documents = await documentService.getByTaskIds(taskIds) || [];

  tasks.forEach((task) => {
    const taskDocuments = documents.filter((doc) => doc.task_id === task.id);

    if (taskDocuments?.length) {
      dealDocuments.push(...taskDocuments);
    }
  });

  return dealDocuments?.length ? dealDocuments : [];
};

const getDocumentRequestsByDealId = async (dealId, user) => {
  const documentRequests = (user && userHelpers.isClient(user))
    ? await taskService.getClientDocumentRequestsByDealId(dealId)
    : await taskService.getDocumentRequestsByDealId(dealId);

  if (!documentRequests) {
    return [];
  }

  const milestones = await getMilestonesWithTasksByDealId(dealId);
  const milestoneIds = milestones?.map((m) => m.id);

  const filteredDocumentRequests = documentRequests
    ?.filter((documentRequest) => milestoneIds.includes(documentRequest.milestone_id));
  const taskIds = filteredDocumentRequests.map((task) => task.id);
  const documents = await documentService.getByTaskIds(taskIds) || [];

  return Promise.all(filteredDocumentRequests.map(async (task) => {
    const taskDocuments = documents.filter((doc) => doc.task_id === task.id);

    const document = taskDocuments?.[0];

    return {
      ...task,
      document: document && await documentOperations.extendDocument(document),
    };
  }));
};

export const getDocumentsByDealId = async (dealId, user) => {
  if (!dealId) {
    return {
      documents: [],
      documentRequests: [],
    };
  }

  const [transactionDocuments, taskDocuments, documentRequests] = await Promise.all([
    getTransactionDocuments(dealId, user),
    getTaskDocumentsByDealId(dealId, user),
    getDocumentRequestsByDealId(dealId, user),
  ]);

  return {
    documents: await documentOperations.extentDocuments([...transactionDocuments, ...taskDocuments]),
    documentRequests,
  };
};
